import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';

const stats = [
  { value: '50K+', label: 'Nigerian Learners', icon: 'ri-group-line' },
  { value: '200+', label: 'Expert Courses', icon: 'ri-book-open-line' },
  { value: '95%', label: 'Completion Rate', icon: 'ri-trophy-line' },
  { value: '4.9★', label: 'Average Rating', icon: 'ri-star-fill' },
];

export default function HeroSection({ content }) {
  const { isAuthenticated } = useAuth();

  const badgeText = content?.badgeText || 'Built for Nigeria. Trusted across Africa.';
  const title = content?.title || 'Learn the Skills That';
  const titleHighlight = content?.titleHighlight || 'Naija Demands Today';
  const description = content?.description || 'From Lagos to Kano, Abuja to Port Harcourt — join thousands of Nigerians mastering tech, design, business, and digital skills to build careers and businesses that thrive.';
  const primaryCtaText = content?.primaryCtaText || 'Explore Courses';
  const primaryCtaLink = content?.primaryCtaLink || '/courses';
  const secondaryCtaText = content?.secondaryCtaText || (isAuthenticated ? 'Go to Dashboard' : 'Start for Free');
  const secondaryCtaLink = content?.secondaryCtaLink || (isAuthenticated ? '/dashboard' : '/signup');
  const backgroundImage = content?.backgroundImage || 'https://readdy.ai/api/search-image?query=young%20Nigerian%20African%20students%20learning%20together%20in%20bright%20modern%20Lagos%20coworking%20space%20laptops%20tablets%20smiling%20engaged%20vibrant%20colourful%20productive%20atmosphere%20Nigerian%20flag%20colours%20green%20white&width=1440&height=900&seq=nghero1&orientation=landscape';
  const statsList = content?.stats && content.stats.length > 0 ? content.stats : stats;

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-slate-950">
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="Nigerian students learning"
          className="w-full h-full object-cover object-top opacity-35 filter brightness-75"
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/40 text-teal-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 backdrop-blur-md shadow-sm">
            <i className="ri-map-pin-line text-teal-400" />
            {badgeText}
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.15] mb-6 drop-shadow-md">
            {title}<br />
            <span className="text-teal-400 bg-gradient-to-r from-teal-300 to-emerald-400 bg-clip-text text-transparent">
              {titleHighlight}
            </span>
          </h1>

          <p className="text-base sm:text-lg text-slate-200 leading-relaxed mb-8 max-w-xl font-normal drop-shadow-sm">
            {description}
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12">
            <Link
              to={primaryCtaLink}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25 active:scale-95"
            >
              <i className="ri-play-circle-line text-xl" />
              {primaryCtaText}
            </Link>
            <Link
              to={secondaryCtaLink}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm active:scale-95"
            >
              {secondaryCtaText}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 max-w-xl">
            {statsList.map(stat => (
              <div key={stat.label} className="bg-slate-900/80 backdrop-blur-md border border-slate-700/60 rounded-xl p-3.5 text-center shadow-md">
                <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5">
                  <i className={`${stat.icon || 'ri-star-fill'} text-teal-400 text-lg`} />
                </div>
                <div className="text-xl font-bold text-white tracking-tight">{stat.value}</div>
                <div className="text-xs text-slate-300 font-medium mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
